import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { usePatientReportSettings, useSavePatientReportSettings } from '@/hooks/usePatientReports';
import type { PatientReportLetterheadMode } from '@/lib/patientReports/model';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Toggle } from '@/components/ui/Toggle';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { PatientReportTemplateSettings } from '@/components/patientReports/PatientReportTemplateSettings';
import { IntegrationsBackLink } from '@/pages/settings/ClinicIntegrations';
import { SectionCard, SettingsLoading, SettingsSubpageHeader } from './settingsChrome';

export function ClinicPatientReports() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isPlatformAdmin, isClinicAdmin } = useAuth();
  const settings = usePatientReportSettings(activeClinicId);
  const save = useSavePatientReportSettings(activeClinicId);
  const [enabled, setEnabled] = useState(false);
  const [letterhead, setLetterhead] = useState<PatientReportLetterheadMode>('none');
  const [file, setFile] = useState<File | null>(null);

  const savedEnabled = settings.data?.enabled;
  const savedLetterhead = settings.data?.letterhead;
  const savedPath = settings.data?.letterheadStoragePath ?? null;
  useEffect(() => {
    if (savedEnabled === undefined || savedLetterhead === undefined) return;
    setEnabled(savedEnabled);
    setLetterhead(savedLetterhead);
    setFile(null);
  }, [savedEnabled, savedLetterhead, savedPath]);

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canEdit || !settings.data) return;
    if (letterhead === 'custom' && !file && !settings.data.letterheadStoragePath) {
      toast.error(t('settings.integrations.patient_reports.file_required'));
      return;
    }
    try {
      await save.mutateAsync({
        enabled,
        letterhead,
        file,
        existingPath: settings.data.letterheadStoragePath,
        existingMime: settings.data.letterheadMime,
      });
      toast.success(t('settings.integrations.patient_reports.saved'));
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      toast.error(
        message === 'letterhead-type'
          ? t('settings.integrations.patient_reports.file_type')
          : t('settings.integrations.patient_reports.save_error'),
      );
    }
  };

  if (settings.isLoading) return <SettingsLoading />;
  if (settings.error)
    return <ErrorAlert message={t('settings.integrations.patient_reports.load_error')} />;

  return (
    <div className="flex min-w-0 flex-col gap-s4">
      <form onSubmit={(event) => void onSubmit(event)} className="flex min-w-0 flex-col gap-s4">
        <IntegrationsBackLink />
        <SettingsSubpageHeader
          icon={<FileText className="h-5 w-5" aria-hidden />}
          title={t('settings.integrations.cards.patient_reports_title')}
          subtitle={t('settings.integrations.patient_reports.subtitle')}
        />
        <SectionCard
          title={t('settings.integrations.patient_reports.enable_title')}
          subtitle={t('settings.integrations.patient_reports.enable_desc')}
        >
          <Toggle
            checked={enabled}
            disabled={!canEdit || save.isPending}
            onCheckedChange={setEnabled}
            label={t('settings.integrations.patient_reports.enable')}
            hint={t('settings.integrations.patient_reports.enable_hint')}
          />
          {!canEdit ? (
            <p className={cn(TYPE.small, 'mt-s3 text-ds-muted')}>
              {t('settings.integrations.patient_reports.admin_only')}
            </p>
          ) : null}
        </SectionCard>

        <SectionCard
          title={t('settings.integrations.patient_reports.letterhead_title')}
          subtitle={t('settings.integrations.patient_reports.letterhead_hint')}
        >
          <fieldset className="flex flex-col gap-s3" disabled={!canEdit || save.isPending}>
            <LetterheadChoice
              value="none"
              current={letterhead}
              label={t('settings.integrations.patient_reports.letterhead_none')}
              onChange={setLetterhead}
            />
            <LetterheadChoice
              value="invoice"
              current={letterhead}
              label={t('settings.integrations.patient_reports.letterhead_invoice')}
              hint={t('settings.integrations.patient_reports.letterhead_invoice_hint')}
              onChange={setLetterhead}
            />
            <LetterheadChoice
              value="custom"
              current={letterhead}
              label={t('settings.integrations.patient_reports.letterhead_custom')}
              hint={t('settings.integrations.patient_reports.letterhead_custom_hint')}
              onChange={setLetterhead}
            />
          </fieldset>
          {letterhead === 'custom' ? (
            <div className="mt-s4">
              <label
                className={cn(TYPE.small, 'font-medium text-ds-text')}
                htmlFor="patient-report-letterhead"
              >
                {t('settings.integrations.patient_reports.file_label')}
              </label>
              <input
                id="patient-report-letterhead"
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                disabled={!canEdit || save.isPending}
                className="mt-s2 block w-full text-sm text-ds-text"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
              {settings.data?.letterheadStoragePath ? (
                <p className={cn(TYPE.small, 'mt-s2 text-ds-muted')}>
                  {t('settings.integrations.patient_reports.file_saved')}
                </p>
              ) : null}
            </div>
          ) : null}
          {canEdit ? (
            <div className="mt-s5 flex justify-end">
              <Button type="submit" loading={save.isPending}>
                {t('settings.integrations.patient_reports.save')}
              </Button>
            </div>
          ) : null}
        </SectionCard>
      </form>
      <PatientReportTemplateSettings
        clinicId={activeClinicId}
        canEdit={canEdit}
        clinicLetterhead={letterhead}
      />
    </div>
  );
}

function LetterheadChoice({
  value,
  current,
  label,
  hint,
  onChange,
}: {
  value: PatientReportLetterheadMode;
  current: PatientReportLetterheadMode;
  label: string;
  hint?: string;
  onChange: (value: PatientReportLetterheadMode) => void;
}) {
  return (
    <label className="flex items-start gap-s3">
      <input
        type="radio"
        name="patient-report-letterhead-mode"
        className="mt-1"
        checked={current === value}
        onChange={() => onChange(value)}
      />
      <span>
        <span className={cn(TYPE.body, 'text-ds-text')}>{label}</span>
        {hint ? <span className={cn(TYPE.small, 'mt-s1 block text-ds-muted')}>{hint}</span> : null}
      </span>
    </label>
  );
}
